import { FEATURED_TESTIMONIAL, TESTIMONIALS } from '@/data/testimonials';
import { Eyebrow } from '@/components/Atoms';

/**
 * Wall of Trust — premium testimonial section combining:
 *  • emotional proof (featured large-format testimonial)
 *  • business outcomes (service tags)
 *  • authentic client conversations (WhatsApp screenshots)
 *
 * Used on Portfolio and Home. `compact` (Home) caps grid to 3 items.
 */
export default function WallOfTrust({ compact = false }) {
  const items = compact ? TESTIMONIALS.slice(0, 3) : TESTIMONIALS;
  return (
    <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32" data-testid="wall-of-trust">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <Eyebrow>Testimonials</Eyebrow>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-[64px] mt-5 leading-[1.04] tracking-tight max-w-3xl">
          Trusted by<br/>
          <span className="font-serif-italic italic text-[#C9A24D]">Business Owners.</span>
        </h2>
        <p className="font-serif-italic italic text-lg lg:text-xl text-[#E6CFC7]/80 mt-6 max-w-2xl">
          Real conversations, shared with permission.
        </p>

        {/* ── Featured testimonial — large quote format ── */}
        <article
          data-testid="wall-of-trust-featured"
          className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
        >
          <div className="lg:col-span-7 order-2 lg:order-1">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#1C1C1C] bg-[#C9A24D] px-3 py-1.5">
              {FEATURED_TESTIMONIAL.tag}
            </span>
            <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-[44px] mt-6 leading-[1.1] tracking-tight">
              {FEATURED_TESTIMONIAL.title}
            </h3>
            <p className="mt-5 text-base lg:text-lg text-[#E6CFC7]/80 leading-relaxed max-w-xl">
              {FEATURED_TESTIMONIAL.context}
            </p>
            <blockquote className="mt-10 border-l-2 border-[#C9A24D] pl-6 font-serif-italic italic text-xl lg:text-2xl leading-[1.5] text-[#F4EFEA]">
              &ldquo;{FEATURED_TESTIMONIAL.quote}&rdquo;
            </blockquote>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <img
              src={FEATURED_TESTIMONIAL.image}
              alt={FEATURED_TESTIMONIAL.title}
              loading="lazy"
              className="w-full h-auto block"
            />
          </div>
        </article>

        {/* ── Grid of testimonials ── */}
        <div className="mt-20 lg:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16">
          {items.map((t) => (
            <figure
              key={t.id}
              data-testid={`wa-screenshot-${t.id}`}
              className="space-y-6"
            >
              {/* TAG */}
              <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-[#C9A24D] border border-[#C9A24D]/60 px-3 py-1.5">
                {t.tag}
              </span>

              {/* TITLE + SUMMARY (primary, 30-40% larger) */}
              <figcaption>
                <h3 className="font-serif-display text-2xl lg:text-[28px] leading-[1.2] text-[#F4EFEA]">
                  {t.title}
                </h3>
                <p className="mt-4 text-base lg:text-lg leading-relaxed text-[#E6CFC7]/85 font-serif-italic italic">
                  {t.summary}
                </p>
              </figcaption>

              {/* SCREENSHOT (secondary, retained for authenticity) */}
              <img
                src={t.image}
                alt={t.title}
                loading="lazy"
                className="w-full h-auto block opacity-95 hover:opacity-100 transition-opacity"
              />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
